import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Color-changing word component
export const ColorChangingWord = ({ children, colors = ['#8b5cf6', '#3b82f6', '#ec4899', '#10b981', '#f59e0b'], className = "", delay = 0 }) => {
    const [currentColor, setCurrentColor] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentColor((prev) => (prev + 1) % colors.length);
        }, 2000 + delay * 200);

        return () => clearInterval(interval);
    }, [colors.length, delay]);

    return (
        <motion.span
            className={className}
            animate={{
                color: colors[currentColor],
            }}
            transition={{
                duration: 0.5,
                ease: 'easeInOut',
            }}
        >
            {children}
        </motion.span>
    );
};

// Synonym rotator - typing effect that cycles through synonyms
export const SynonymRotator = ({ synonyms, className = "", interval = 4000, typingSpeed = 80 }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [displayText, setDisplayText] = useState('');

    useEffect(() => {
        let timeout;
        let charIndex = 0;
        let isDeleting = false;
        const currentWord = synonyms[currentIndex];

        const type = () => {
            if (!isDeleting && charIndex <= currentWord.length) {
                // Typing
                setDisplayText(currentWord.substring(0, charIndex));
                charIndex++;
                timeout = setTimeout(type, typingSpeed);
            } else if (isDeleting && charIndex >= 0) {
                // Deleting
                setDisplayText(currentWord.substring(0, charIndex));
                charIndex--;
                timeout = setTimeout(type, typingSpeed / 2);
            } else if (!isDeleting && charIndex > currentWord.length) {
                // Finished typing, wait then start deleting
                timeout = setTimeout(() => {
                    isDeleting = true;
                    type();
                }, interval);
            } else if (isDeleting && charIndex < 0) {
                // Finished deleting, move to next word
                isDeleting = false;
                setCurrentIndex((prev) => (prev + 1) % synonyms.length);
                charIndex = 0;
                timeout = setTimeout(type, typingSpeed);
            }
        };

        timeout = setTimeout(type, typingSpeed);

        return () => clearTimeout(timeout);
    }, [currentIndex, synonyms, interval, typingSpeed]);

    return (
        <span className={className}>
            {displayText}
            <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.8, repeat: Infinity, ease: 'easeInOut' }}
                className="inline-block"
            >
                |
            </motion.span>
        </span>
    );
};

// Decrypt text - random letters that settle into the correct word
export const DecryptText = ({ text, className = "", delay = 0, speed = 50 }) => {
    const [displayText, setDisplayText] = useState('');
    const [isDecrypting, setIsDecrypting] = useState(true);
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    useEffect(() => {
        let timeout;
        let iterations = 0;
        const maxIterations = text.length * 3; // 3 cycles of random letters per character

        const decrypt = () => {
            if (iterations < maxIterations) {
                setDisplayText(
                    text
                        .split('')
                        .map((char, index) => {
                            if (index < Math.floor(iterations / 3)) {
                                return text[index]; // Already decrypted
                            }
                            if (char === ' ') return ' '; // Keep spaces
                            return chars[Math.floor(Math.random() * chars.length)];
                        })
                        .join('')
                );
                iterations++;
                timeout = setTimeout(decrypt, speed);
            } else {
                setDisplayText(text);
                setIsDecrypting(false);
            }
        };

        timeout = setTimeout(() => {
            decrypt();
        }, delay);

        return () => clearTimeout(timeout);
    }, [text, delay, speed]);

    return (
        <motion.span
            className={className}
            animate={isDecrypting ? {} : { color: ['#8b5cf6', '#3b82f6', '#ec4899', '#8b5cf6'] }}
            transition={isDecrypting ? {} : { duration: 1, repeat: Infinity, ease: 'linear' }}
        >
            {displayText || text}
        </motion.span>
    );
};

// Animated gradient text component (existing, keeping for compatibility)
export const AnimatedGradientText = ({ children, className = "" }) => {
    return (
        <motion.span
            className={`inline-block ${className}`}
            animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            }}
            transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear',
            }}
            style={{
                background: 'linear-gradient(90deg, #8b5cf6, #3b82f6, #ec4899, #8b5cf6)',
                backgroundSize: '200% 200%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
            }}
        >
            {children}
        </motion.span>
    );
};

